<script lang="ts">
  import { link } from "svelte-routing";
  import {
    Row,
    Col,
    Input,
    Button,
  } from "sveltestrap"
  import { connected, chainId, selectedAccount, web3 } from "svelte-web3"
  import TokenInput from "./TokenInput.svelte"
  import CurrencyBadge from "./CurrencyBadge.svelte"
  import { getPairDetail } from "../stores/query"
  import PilTooltip from "./PilTooltip.svelte"
  import { nFormatter, shortenAddress, timeConverter, txLink } from "../utils/utils"
  import { currentChainId, multipliers, pairDetail, tokenPrice, viewCoreContract } from "../stores/store"
  import WalletConnect from "./WalletConnect.svelte"
  import { ERC20ABI } from "../abis/erc20"
  import { COREABI } from "../abis/core"
  import { CONFIG } from "../config"

  export let isNft: boolean;
  let payAmount = 0;
  let baseBalance = 0;
  let baseToken = 'PIL';
  let minPrice = 0;
  let phase = 'input' // input -> confirm -> submit
  let isApproved = false;
  let txHash = '';
  let approveDisabled = false;
  let confirmDisabled = false;

  const inputPayMount = (e: Event) => {
    if (e !== undefined) {
      const value = Number.parseFloat((e.target as HTMLInputElement).value);
      if (isNaN(value)) {
        payAmount = 0;
      } else {
        payAmount = value;
      }
    }
  };

  const getBaseBalance = async () => {
    if (!$connected || $getPairDetail.fetching) return 0;
    const tokenContract = new $web3.eth.Contract(ERC20ABI, $getPairDetail.data.pair.baseToken.id);
    baseBalance = Number.parseFloat(await tokenContract.methods.balanceOf($selectedAccount).call()) / 10 ** Number.parseInt($getPairDetail.data.pair.baseToken.decimals);
    const allowance = $web3.utils.toBN(await tokenContract.methods.allowance($selectedAccount, CONFIG[$chainId].CORE_ADDRESS).call());
    isApproved = allowance.gt($web3.utils.toBN(0));
  }

  const approve = async () => {
    const tokenContract = new $web3.eth.Contract(ERC20ABI, $getPairDetail.data.pair.baseToken.id);
    tokenContract.methods.approve(
      CONFIG[$chainId].CORE_ADDRESS,
      '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
    ).send({ from: $selectedAccount })
      .on('transactionHash', hash => {
        console.log(hash);
        approveDisabled = true;
      })
      .on('receipt', receipt => {
        console.log(receipt);
        isApproved = true;
        approveDisabled = false;
      })
  }

  const quote = async () => {
    if ($viewCoreContract === undefined) return 0;
    let result
    if (isNft) {
      result = (await $viewCoreContract.methods.quoteNft($getPairDetail.data.pair.id).call())[0];
    } else {
      result = await $viewCoreContract.methods.quoteMetaNft($getPairDetail.data.pair.id).call();
    }
    minPrice = Number.parseInt(result) / Math.pow(10, $getPairDetail.data.pair.baseToken.decimals)
  }

  const bidNft = async () => {
    const pilgrim = new $web3.eth.Contract(COREABI, CONFIG[$chainId].CORE_ADDRESS);
    const decimals = $getPairDetail.data.pair.baseToken.decimals;
    const amount = $web3.utils.toBN((payAmount * Math.pow(10, decimals)).toLocaleString('fullwide', { useGrouping: false }));
    const method = isNft ? pilgrim.methods.bidNft : pilgrim.methods.bidMetaNft
    method($getPairDetail.data.pair.id, amount, Math.round(Date.now() / 1000 + 30000)).send({ from: $selectedAccount })
      .on('transactionHash', hash => {
        console.log(hash);
        txHash = hash;
        confirmDisabled = true;
      })
      .on('receipt', receipt => {
        console.log(receipt);
        phase = 'submit';
        confirmDisabled = false;
      })
  }

  const reset = () => {
    payAmount = 0;
    phase = 'input';
  }

  $: if (!$getPairDetail.fetching) {
    baseToken = $getPairDetail.data.pair.baseToken.symbol;
  }

  $: if (!$getPairDetail.fetching && $viewCoreContract !== undefined) {
    quote();
  }

  $: if ($connected && !$getPairDetail.fetching) {
    getBaseBalance();
  }

</script>

{#if phase === 'input'}
  <Row class="text-center trade-section">
    <Col>
      <div class="trade-detail-label">
        PIL APR
        <PilTooltip tooltipId="trade-pil-apy">Annualized PIL returns from this pool.</PilTooltip>
      </div>
      <div class="trade-detail-value">
        {nFormatter($pairDetail.yearPairRewardPerRound * $tokenPrice.PIL / ($pairDetail.roundTokenPrice * $tokenPrice[$pairDetail.denominator.symbol]) * 100, 2)}%
      </div>
    </Col>
    <Col>
      <div class="trade-detail-label">
        Multiplier
        <PilTooltip tooltipId="trade-multiplier">Rewards multiplier over base PIL emissions for this pool.</PilTooltip>
      </div>
      <div class="trade-detail-value">{$multipliers[baseToken]}x</div>
    </Col>
  </Row>
  <Row class="trade-margin"/>
  <Row class="trade-input-label trade-section">
    Max Buyout Price
    <PilTooltip tooltipId="trade-buyout-price">Full buyouts are only available at a higher price than the current spot price determined by the AMM. This is the maximum price you can pay when your bid is accepted.</PilTooltip>
    <TokenInput
      currency={baseToken}
      onInput={inputPayMount}
      balance={baseBalance}
      valid={payAmount <= baseBalance && payAmount >= minPrice}
      value={payAmount}
    />
  </Row>
  {#if payAmount > baseBalance}
    <Row class="trade-section gx-3">
      <Col class="col-auto">
        <img class="icon-small" src="/icons/warning.svg" alt="placeholder"/>
      </Col>
      <Col class="warning-msg">
        You have insufficient fund in your wallet.<br/>
        Deposit more funds in order to make a successful transaction.
      </Col>
    </Row>
  {:else if payAmount > 0 && payAmount < minPrice}
    <Row class="trade-section gx-3">
      <Col class="col-auto">
        <img class="icon-small" src="/icons/warning.svg" alt="placeholder"/>
      </Col>
      <Col class="warning-msg">
        Your offer price has to be higher than the minimum price.
      </Col>
    </Row>
  {/if}
  <Row class="trade-section">
    <Col class="trade-min-price-label col-auto">
      Minimum price
      <img class="icon-small" src="/icons/question.svg"/>
    </Col>
    <Col class="trade-min-price text-end">
      {Number.parseFloat((minPrice).toFixed(3)).toLocaleString('en')} {baseToken}
    </Col>
  </Row>
  <Row class="trade-section trade-desc gx-1">
    <Col class="col-auto">
      <img class="icon-small" src="/icons/bulb.svg"/>
    </Col>
    <Col>
      The minimum price reflect the NFT’s current value. To submit a valid offer, your offer price has to be higher than the minimum price.
      <br/>
      <a class="trade-desc-link" href="#">Learn more about Pilgrim's bid offer process</a>
    </Col>
  </Row>
  <Row class="trade-section">
    {#if $connected}
      <Button class="trade-button" on:click={() => phase = 'confirm'} disabled={true}>Submit Offer</Button>
    {:else}
      <WalletConnect className="trade-button">Connect to a wallet</WalletConnect>
    {/if}
  </Row>
{:else if phase === 'confirm'}
  <Row class="trade-section gx-0 align-items-center">
    <Col class="col-auto">
      <img class="icon-large" src="/icons/arrow-left.svg" alt="placeholder" on:click={() => phase = 'input'}/>
    </Col>
    <Col class="trade-phase-back">
      Go back to offer window
    </Col>
  </Row>
  <Row class="trade-margin"/>
  <Row class="trade-section trade-phase-title">
    Confirm Your Offer
  </Row>
  <Row class="trade-input-label trade-section">
    Offer price
  </Row>
  <Row class="trade-section trade-confirm-amount gx-0">
    <Col class="col-auto trade-confirm-value">
      {payAmount}
    </Col>
    <CurrencyBadge currency={baseToken}/>
  </Row>
  <Row class="trade-input-label trade-section">
    Offer is valid until
  </Row>
  <Row class="trade-section trade-confirm-amount gx-0">
    <Col class="col-auto trade-offer-timestamp">
      {timeConverter(Date.now() + 1000 * 60 * 60 * 6)}
    </Col>
  </Row>
  <Row class="trade-section gx-3 trade-warning-box">
    <Col class="col-auto">
      <img class="icon-small" src="/icons/warning.svg" alt="placeholder"/>
    </Col>
    <Col class="warning-msg">
      Once this offer is submitted, you are unable to submit a new offer until the original offer is accepted or expires.
    </Col>
  </Row>
  {#if $connected && !isApproved}
    <Row class="trade-section">
      <Button class="trade-button" on:click={approve} disabled={true}>Allow Pilgrim to use your {$getPairDetail.data.pair.baseToken.symbol}</Button>
    </Row>
  {/if}
  <Row class="trade-section">
    <Button class="trade-button" on:click={bidNft} disabled={true}>Confirm Offer</Button>
  </Row>
{:else if phase === 'submit'}
<Row class="trade-margin"/>
<Row class="trade-section gx-0 trade-phase-title text-end">
  <Col class="text-end">
    <img class="icon-large" src="/icons/close.svg" alt="placeholder" on:click={reset}/>
  </Col>
</Row>
<Row class="trade-section gx-0 trade-phase-title ">
  <Col class="col-auto trade-announce-icon">
    <img class="icon-medium" src="/icons/check-circle.svg" alt="placeholder"/>
  </Col>
  <Col>
    <Row class="align-items-center">
      <Col>
        Your offer has been submitted
      </Col>
    </Row>
    <Row class="trade-result">
      <Col>
        Your offer price is <span class="primary">{payAmount} {baseToken}</span>
      </Col>
    </Row>
    <Row class="trade-result">
      <Col>
        Your offer is valid until <span class="primary">Jan 4, 2022 8:45 PM</span>
      </Col>
    </Row>
    <Row class="trade-result">
      <Col>
        Transaction ID: <a href={txLink(txHash, $currentChainId)} target="_blank">{shortenAddress(txHash)}</a>
      </Col>
    </Row>
    <Row class="trade-result">
      <Col>
        <a href="/portfolio/bids" use:link>
          <Button class="button-line view-position-button">View my Offers</Button>
        </a>
      </Col>
    </Row>
  </Col>
</Row>
{/if}
